import { Injectable, OnDestroy, inject } from '@angular/core';
import { Router } from '@angular/router';
import { ShareFilesDialogService } from '@dntlab/share-files-dialog';
import { TranslateService } from '@ngx-translate/core';
import FileSystemItem from 'devextreme/file_management/file_system_item';
import {
  EMPTY,
  Observable,
  Subject,
  Subscription,
  catchError,
  firstValueFrom,
  map,
  of,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { SessionService } from 'src/app/auth/session.service';
import { getCurrentTime, promiseWithCatch } from 'src/app/common/app.helpers';
import AppValues from 'src/app/common/app.values';
import ProjectLinks from 'src/app/common/project.links';
import { BlockedUsersService } from 'src/app/common/services/blocked-users.service';
import { DataItem, FileDataItem } from 'src/app/interfaces';
import {
  FileManagerPermissions,
  FileSharedEvent,
  MenuItemClickContent,
  OnFileUploaded,
  UploadFileContent,
  XhrUploadedFile,
} from 'src/app/interfaces/file.interface';
import { DialogActionTypes } from 'src/app/notifications/dialogs/dialogs.enum';
import { NotificationContextAttributesService } from 'src/app/notifications/notifications-context-attributes.service';
import { NotificationService } from 'src/app/notifications/notifications.service';
import { fileDownloadContext } from 'src/app/services/api-error';
import { FoldersResponseInner } from 'src/app/swagger-api/model/foldersResponseInner';
import { SecurityDescriptor } from 'src/app/swagger-api/model/securityDescriptor';
import {
  CreateFolderRequest,
  FileDescriptor,
  FilePermissions,
  FileSizeResponse,
  FolderDescriptor,
  FoldersResponse,
  GenerateDownloadFileUrlResponse,
  GenerateFileRequest,
  GenerateFileResponse,
  ListOfBasicUsers,
  DefaultService as RestApiService,
} from '../../swagger-api';
import { FileManagerAdapter } from './dntl-filemanager/filemanager-adapter/filemanager-adapter';
import { FileType } from './file-type';
import { ActivityLog, FMActionTypes, FileCategories, FileOwnershipTypes, TypeOfAction } from './files-manager.enum';
import { FilesValidationHandler } from './files-validation.handler';
import { ShareFilesHandler } from './share-files.handler';

@Injectable()
export class FileManagerService implements OnDestroy {
  public get fileShared(): Observable<FileSharedEvent[]> {
    return this.fileShared$.asObservable();
  }

  public get getFileStorage(): Observable<FoldersResponse> {
    return this.getFileStorage$.asObservable();
  }

  public get onFileChanges(): Observable<boolean> {
    return this.changesCompleted$.asObservable();
  }

  public get onFolderCreated(): Observable<OnFileUploaded> {
    return this.folderCreated$.asObservable();
  }

  public get onFileLoaded(): Observable<OnFileUploaded> {
    return this.fileLoaded$.asObservable();
  }

  public get onDownloadLinkReceived(): Observable<{ url: string; item: FileSystemItem }[]> {
    return this.downloadLinkChanges$.asObservable();
  }

  public get onStorageSizeChanged(): Observable<FileSizeResponse> {
    return this.storageSizeChanges$.asObservable();
  }

  public get allowedFileExtensions(): string[] {
    return this.filesValidationHandler.allowedFileExtensions;
  }

  public get permissions(): FileManagerPermissions {
    return {
      copy: false,
      create: false,
      delete: false,
      download: false,
      move: false,
      rename: false,
      upload: false,
    };
  }

  public set currentOwnershipType(type: string) {
    this.currentFileOwnershipType = FileOwnershipTypes[type] as FileOwnershipTypes;
  }

  public folders: FoldersResponse;
  public fileTypeContext: FileType = new FileType();
  public xmlUploadFiles: XhrUploadedFile[] = [];
  public profileImage: FoldersResponseInner;
  public uploadingProgressFiles: string[] = [];
  private getFileStorage$: Subject<FoldersResponse> = new Subject();
  private fileShared$: Subject<FileSharedEvent[]> = new Subject();
  private changesCompleted$: Subject<boolean> = new Subject();
  private fileLoaded$: Subject<OnFileUploaded> = new Subject();
  private folderCreated$: Subject<OnFileUploaded> = new Subject();
  private downloadLinkChanges$: Subject<{ url: string; item: FileSystemItem }[]> = new Subject();
  private storageSizeChanges$: Subject<FileSizeResponse> = new Subject();
  private shareFilesHandler: ShareFilesHandler;

  private currentFileCategory: FileCategories = FileCategories.documents;
  private currentFileOwnershipType: FileOwnershipTypes = FileOwnershipTypes.manager;

  private allUsers: ListOfBasicUsers = { items: [] };
  private allSharedUsersId: string[] = [];
  private uploadingFilesName: string[] = [];
  private readonly BYTE_IN_GB: number = AppValues.BYTE_IN_GB;

  private router = inject(Router);
  private apiService = inject(RestApiService);
  private notificationService = inject(NotificationService);
  private modalAttributesService = inject(NotificationContextAttributesService);
  private sessionService = inject(SessionService);
  private adapter = inject(FileManagerAdapter);
  private dialogService = inject(ShareFilesDialogService);
  private translateService = inject(TranslateService);
  private filesValidationHandler = inject(FilesValidationHandler);
  private blockedUsers = inject(BlockedUsersService);

  public constructor() {
    this.shareFilesHandler = new ShareFilesHandler(
      this.fileShared$,
      this.changesCompleted$,
      this.apiService,
      this.dialogService,
      this.sessionService,
      this.filesValidationHandler,
      this.translateService,
      this.blockedUsers,
    );

    this.sessionService.sessionChanges.subscribe(() => {
      this.setProfileImage(this.folders);
    });
  }

  public ngOnDestroy(): void {
    if (this.changesCompleted$) {
      this.changesCompleted$.complete();
    }

    if (this.fileLoaded$) {
      this.fileLoaded$.complete();
    }

    if (this.folderCreated$) {
      this.folderCreated$.complete();
    }

    if (this.storageSizeChanges$) {
      this.storageSizeChanges$.complete();
    }

    if (this.downloadLinkChanges$) {
      this.downloadLinkChanges$.complete();
    }
  }

  public onAction(content: MenuItemClickContent): void {
    switch (content.item.name) {
      case FMActionTypes.CHANGE:
        this.uploadNewVersion(content.items);
        break;
      case FMActionTypes.DELETE:
        this.deleteFiles(content.items);
        break;
      case FMActionTypes.SHARE:
        this.shareFiles(content.items);
        break;
      case FMActionTypes.UNSHARED:
        this.unshareFiles(content.items);
        break;
      case FMActionTypes.DOWNLOAD:
        this.downloadFiles(content.selected, true);
        break;
      case FMActionTypes.ACTIVITIES:
        this.showActivities(content.items);
        break;
    }
  }

  public getItemsByPath(activeFileCategory: ActivityLog | FileCategories, path?: string): Promise<FileSystemItem[]> {
    if (activeFileCategory === ActivityLog.activities) {
      return Promise.reject(`Unsupported file category: ${activeFileCategory}`);
    }

    const query: Observable<FoldersResponse> = !path
      ? this.apiService.restFileStorageFolderGetRoot()
      : this.apiService.restFileStorageFolderGet(path);

    return firstValueFrom(
      query.pipe(catchError(() => of([]))).pipe(
        tap((response: FoldersResponse) => {
          if (!path) {
            this.folders = response;
          }
          this.getFileStorageSize();
          this.setProfileImage(response);
          this.getSharedUsersInfo(response);

          this.getFileStorage$.next(response);
        }),

        map((response: FoldersResponse) => this.filterFiles(response)),
      ),
    );
  }

  public setProfileImage(response: FoldersResponseInner[] = []): void {
    if (this.sessionService.user) {
      this.profileImage = response.find((item: FoldersResponseInner) => {
        return (
          this.sessionService.user.image_url === (item.entity as FileDescriptor).public_url &&
          this.fileTypeContext.isImage((item.entity as FileDescriptor).content_type)
        );
      });
    }
  }

  public setCategory(category: string): void {
    if (!FileCategories[category]) {
      promiseWithCatch(
        this.router.navigate([ProjectLinks.LINK_FILES_MANAGER], {
          queryParams: { type: FileCategories.documents },
        }),
      );
      return;
    }

    this.currentFileCategory = FileCategories[category] as FileCategories;
    this.changesCompleted$.next(true);
  }

  public setTabType(type: string): void {
    if (!FileOwnershipTypes[type]) {
      promiseWithCatch(
        this.router.navigate([`${ProjectLinks.LINK_FILES}/${FileOwnershipTypes.manager}`], {
          queryParams: { type: FileCategories.documents },
        }),
      );
      return;
    }

    this.currentFileOwnershipType = FileOwnershipTypes[type] as FileOwnershipTypes;
    this.changesCompleted$.next(true);
  }

  public checkUnusedStorageSize(files: File[], unusedStorageSize: number): boolean {
    let uploadingFilesSize = 0;
    Array.from(files).forEach((file: File) => {
      this.uploadingFilesName.push(file.name);
      uploadingFilesSize += file.size;
    });
    if (uploadingFilesSize && this.getUploadingFileSizeInGb(uploadingFilesSize) > unusedStorageSize) {
      this.showUploadModalError(files);
      return true;
    }
    this.uploadingFilesName = [];
    return false;
  }

  public uploadFiles(
    files: File[],
    checkFilesValidity = true,
    generateUploadUrl = true,
    folderDescriptorId = '',
  ): number {
    let validFilesCount = 0;
    Array.from(files).forEach((file: File) => {
      if (checkFilesValidity) {
        const description: string = this.filesValidationHandler.getFileValidationDescription(file);

        if (description) {
          this.showFileOptionError(description);
          return;
        }

        validFilesCount++;
      }

      if (generateUploadUrl) {
        this.uploadingProgressFiles.push(file.name);
        this.generateUploadUrl({ file, public: false, folderDescriptorId });
      }
    });

    return validFilesCount;
  }

  /**
   * @description Navigates to file details page.
   * @param { FoldersResponseInner } data
   */
  public navigateTo(data: FoldersResponseInner): void {
    promiseWithCatch(
      this.router.navigateByUrl(`${ProjectLinks.LINK_FILES_DETAILS}/${data.entity['entity_id']}`, {
        state: data,
      }),
    );
  }

  public deleteFile(fileDescriptorId: string): void {
    this.apiService
      .restFileStorageFilesDelete(fileDescriptorId)
      .pipe(catchError(() => EMPTY))
      .subscribe(() => this.changesCompleted$.next(true));
  }

  /**
   * Deletes files
   * @description determines currently selected tab and based on that
   * dynamically sets texting in further dialogDefault call.
   * @param { FoldersResponseInner[] } items
   */
  public deleteFiles(items: FoldersResponseInner[]): void {
    const subs$: Subscription = this.deleteFilesDialog(items).subscribe((response: string | boolean) => {
      subs$.unsubscribe();

      if (response) {
        const fileDescriptorIds: string[] = items
          .filter((item: FoldersResponseInner) => item.entity_kind === 'file_descriptor')
          .map((item: FoldersResponseInner) => item.entity.entity_id);

        const folderDescriptorIds: string[] = items
          .filter((item: FoldersResponseInner) => item.entity_kind === 'folder_descriptor')
          .map((item: FoldersResponseInner) => item.entity.entity_id);

        this.changesCompleted$.next(false);
        this.apiService
          .restFileStorageBulkDeleteDelete(fileDescriptorIds, folderDescriptorIds)
          .pipe(catchError(() => EMPTY))
          .subscribe(() => {
            this.changesCompleted$.next(true);
          });
      }
    });
  }

  public unshareFiles(items: FoldersResponseInner[]): void {
    const subs$: Subscription = this.unshareFilesDialog(items.length > 1).subscribe((response: string | boolean) => {
      subs$.unsubscribe();

      if (response) {
        const fileDescriptorIds: string[] = items
          .filter((item: FoldersResponseInner) => item.entity_kind === 'file_descriptor')
          .map((item: FoldersResponseInner) => item.entity.entity_id);

        this.changesCompleted$.next(false);
        this.apiService
          .restFileStorageUnshareFilesPost({ file_descriptor_ids: fileDescriptorIds })
          .pipe(catchError(() => EMPTY))
          .subscribe(() => {
            this.changesCompleted$.next(true);
          });
      }
    });
  }

  public downloadFiles(items: FileSystemItem[], withoutPreview: boolean): Observable<string> {
    const response$: Subject<string> = new Subject<string>();
    this.apiService
      .restFileStorageGenerateDownloadFileUrlPost((items[0].dataItem as FileDataItem).key)
      .pipe(catchError(() => EMPTY))
      .subscribe((response: GenerateDownloadFileUrlResponse) => {
        response$.next(response.presigned_url);
        if (withoutPreview) {
          this.downloadLinkChanges$.next([{ url: response.presigned_url, item: items[0] }]);
        }
      });

    return response$.asObservable();
  }

  public shareFiles(items: FoldersResponseInner[]): void {
    if (this.allUsers.items.length > 0) {
      this.shareFilesHandler.shareFiles(items, this.allUsers);
    }
  }

  public showActivities(items: FoldersResponseInner[]): void {
    this.notificationService.dialogActivities({
      usersList: this.allUsers.items,
      items: items,
      buttons: this.modalAttributesService.getDefaultOkAttr(),
    });
  }

  public initUsersList(usersId: string[]): void {
    this.apiService
      .restUserIndex(usersId)
      .pipe(catchError(() => of(this.allUsers)))
      .subscribe((users: ListOfBasicUsers) => {
        this.allUsers = users;
      });
  }

  public generateUploadUrl(content: UploadFileContent): void {
    const request: GenerateFileRequest = {
      name: content.file.name,
      content_type: content.file.type,
      size: content.file.size,
      folder_descriptor_id: content.folderDescriptorId,
      is_public: content.public,
    };

    if (!request.folder_descriptor_id) {
      delete request.folder_descriptor_id;
    }

    this.apiService
      .restFileStorageGenerateFileUrlPost(request)
      .pipe(
        catchError(() => {
          this.changesCompleted$.next(true);
          this.fileLoaded$.next({ link: '', controlName: content.controlName });
          return EMPTY;
        }),
      )
      .subscribe((response: GenerateFileResponse) => {
        content.response = response;
        this.uploadFile(content);
        this.changesCompleted$.next(false);
      });
  }

  public validateBeforeCreateFolder(files: File[], controlName: string, name?: string): void {
    const validFileCount: number = this.uploadFiles(files, true, false);
    if (validFileCount !== files.length) {
      this.folderCreated$.next({ link: '', controlName });
      return;
    }

    const request: CreateFolderRequest = { name: name || getCurrentTime().toString() };
    this.createFolder(files, controlName, request);
  }

  public showFileOptionError(description: string): void {
    const subs$: Subscription = this.notificationService
      .dialogDefault({
        title: 'FILE_MANAGER_UPLOAD_FILE_ERROR_TITLE',
        description,
        buttons: this.modalAttributesService.getDefaultOkAttr(),
      })
      .subscribe(() => subs$.unsubscribe());
  }

  public isPublicFiles(): boolean {
    return this.currentFileOwnershipType === FileOwnershipTypes.public;
  }

  public isManagerFiles(): boolean {
    return this.currentFileOwnershipType === FileOwnershipTypes.manager;
  }

  public isSharedFiles(): boolean {
    return this.currentFileOwnershipType === FileOwnershipTypes.shared;
  }

  public showAndDownloadFiles(key: string): Observable<string> {
    return this.apiService.restFileStorageGenerateDownloadFileUrlPost(key, fileDownloadContext(true)).pipe(
      switchMap((response: GenerateDownloadFileUrlResponse) => {
        return of(response.presigned_url);
      }),
      catchError(() => EMPTY),
    );
  }

  public getSharedUsersInfo(response: FoldersResponse): void {
    response.forEach((folder: FoldersResponseInner) => {
      this.allSharedUsersId.push(...folder.security.map((descriptor: SecurityDescriptor) => descriptor.user_id));
    });
    if (this.allSharedUsersId.length > 0) {
      this.initUsersList([...new Set(this.allSharedUsersId)]);
    }
  }

  private getUploadingFileSizeInGb(filesSize: number): number {
    return filesSize / this.BYTE_IN_GB;
  }

  private showUploadModalError(files: File[]): void {
    this.notificationService
      .dialogDefault({
        title: 'FILE_MANAGER_UPLOAD_FILE_ERROR_TITLE',
        description: this.translateService.instant(
          files.length > 1 ? 'MULTIPLE_FILE_UPLOADED_ERROR' : 'ONE_FILE_UPLOADED_ERROR',
          { filesName: this.uploadingFilesName.join(', ') },
        ) as string,
        buttons: [this.modalAttributesService.createDialogButton('BTN_OK_HINT', 'btn-primary')],
      })
      .pipe(take(1))
      .subscribe(() => {
        this.uploadingFilesName = [];
      });
  }

  private createFolder(files: File[], controlName: string, request: CreateFolderRequest): void {
    this.apiService.restFileStorageFolderPost(request).subscribe({
      next: (response: FolderDescriptor) => {
        this.folderCreated$.next({
          link: response.entity_id,
          controlName,
          fileName: this.translateService.instant(
            files.length > 1 ? 'MULTIPLE_FILES_LABEL_HINT' : 'MULTIPLE_FILES_ONE_FILE_LABEL_HINT',
            { count: files.length },
          ) as string,
        });

        this.uploadFiles(Array.from(files), false, true, response.entity_id);
      },
      error: () => {
        this.folderCreated$.next({ link: '', controlName });
      },
    });
  }

  private getFileStorageSize(): void {
    this.apiService
      .restFileStorageSizeGet()
      .pipe(catchError(() => EMPTY))
      .subscribe((response: FileSizeResponse) => this.storageSizeChanges$.next(response));
  }

  private filterFiles(response: FoldersResponse): FileSystemItem[] {
    const items: FileSystemItem[] = this.adapter.foldersResponseAdapter(
      response,
      this.translateService.instant('FILE_MANAGER_FILE_MEMBERS_ONLY_YOU_HINT') as string,
      this.translateService.instant('FILE_MANAGER_FILE_MEMBERS_HINT') as string,
    );

    // TODO: temporary solution until backend not fix it (L2C-1240)

    return items.filter((item: FileSystemItem) => {
      const security: SecurityDescriptor = (item.dataItem as DataItem).item.security.find(
        (elem: SecurityDescriptor) => {
          return elem.user_id === this.sessionService.userId;
        },
      );
      if (security) {
        const creator: boolean =
          (item.dataItem as DataItem).item.entity.creator_account_id === this.sessionService.userId;
        return (
          !item.isDirectory && this.getSecurityDescriptor(item, security, creator) && this.filteredByExtensions(item)
        );
      }
      return [];
    });
  }

  /**
   * @description Filters files depending on selected file ownership type.
   * @returns { FileSystemItem[] }
   * @param item
   * @param security
   * @param creator
   */
  private getSecurityDescriptor(item: FileSystemItem, security: SecurityDescriptor, creator: boolean): boolean {
    return (
      this.isFileOwnershipPublic(item, security) ||
      this.isFileOwnershipManager(item, creator) ||
      this.isFileMembershipShared(creator)
    );
  }

  private isFileOwnershipPublic(item: FileSystemItem, security: SecurityDescriptor): boolean {
    return (
      this.isPublicFiles() &&
      (item.dataItem as DataItem).item.entity.is_public &&
      security.permission === FilePermissions.Owner
    );
  }

  private isFileOwnershipManager(item: FileSystemItem, creator: boolean): boolean {
    return this.isManagerFiles() && !(item.dataItem as DataItem).item.entity.is_public && creator;
  }

  private isFileMembershipShared(creator: boolean): boolean {
    return this.isSharedFiles() && !creator;
  }

  private filteredByExtensions(item: FileSystemItem): boolean {
    const entity: FileDescriptor = (item.dataItem as DataItem).item.entity;

    return (
      entity.content_type.includes(this.currentFileCategory) ||
      this.filesValidationHandler.isAllowedFileExtension(entity, this.currentFileCategory)
    );
  }

  private uploadFile(content: UploadFileContent): void {
    const xhr: XMLHttpRequest = new XMLHttpRequest();

    this.abortUploadOfDeletedFiles();

    xhr.onerror = () => {
      this.showError('FILE_MANAGER_UPLOAD_FILE_ERROR_DESCRIPTION');
      this.fileLoaded$.next({ link: '', controlName: content.controlName });
    };

    xhr.onreadystatechange = () => {
      if (xhr.readyState === XMLHttpRequest.DONE) {
        // Status 0 is for canceled requests
        if (xhr.status === 200 || xhr.status === 204 || xhr.status === 0) {
          this.uploadingProgressFiles = this.uploadingProgressFiles.filter(
            (fileName: string) => fileName !== content.file.name,
          );
          this.uploadingProgressFiles.length === 0 && this.changesCompleted$.next(true);
        } else {
          this.showError('FILE_MANAGER_UPLOAD_FILE_ERROR_DESCRIPTION');
        }
      }
    };

    xhr.onload = () => this.onFileUploaded(content);

    xhr.open('POST', content.response.url, true);
    xhr.send(this.getFormData(content));

    this.xmlUploadFiles.push({ xhr, file: content.file });
  }

  private abortUploadOfDeletedFiles(): void {
    this.getFileStorage.subscribe((folders: FoldersResponse) => {
      const deletedFiles: XhrUploadedFile[] = this.xmlUploadFiles.filter(
        (uploadedFile: XhrUploadedFile) =>
          !folders.some((folder: FoldersResponseInner) => folder.entity.name === uploadedFile.file.name),
      );
      deletedFiles.forEach((deletedFile: { xhr: XMLHttpRequest; file: File }) => deletedFile.xhr.abort());
    });
  }

  private getFormData(content: UploadFileContent): FormData {
    const body: FormData = new FormData();

    // eslint-disable-next-line
    const keys: [string, string | Blob][] = Object.entries(content.response.fields);
    keys.forEach((value: [string, string | Blob]) => body.append(value[0], value[1]));

    body.append('file', content.file);

    return body;
  }

  private onFileUploaded(content: UploadFileContent): void {
    // Update avatar form field value with public_url
    this.fileLoaded$.next({
      link: content.response.public_url,
      controlName: content.controlName,
      fileName: content.file.name,
    });
  }

  private uploadNewVersion(items: FoldersResponseInner[]): void {
    items.forEach(() => {
      // TODO: should be replaced with real api
    });
  }

  private showError(description: string): void {
    this.changesCompleted$.next(false);

    /* It's needed because the file manager should
      have a time to repaint and hide popups */
    setTimeout(() => this.showFileOptionError(description), 1000);
  }

  private deleteFilesDialog(files: FoldersResponseInner[]): Observable<string | boolean> {
    const actionType = TypeOfAction.DELETE;
    const title = files.length > 1 ? 'FILE_MANAGER_DELETE_FILES_TITLE' : 'FILE_MANAGER_DELETE_FILE_TITLE';
    const fileNamesString = files.map((file) => `"${file.entity.name}"`).join(', ');
    const description = this.translateService.instant('FILE_MANAGER_DELETE_FILES_DESCRIPTION', {
      listOfFileNames: fileNamesString,
    }) as string;

    return this.openFileOperationDialog(title, description, actionType);
  }

  private unshareFilesDialog(multipleFiles: boolean): Observable<string | boolean> {
    const actionType = TypeOfAction.UNSHARED;
    const title = this.dialogTitle(actionType);
    const description = this.dialogDescription(actionType, multipleFiles);
    return this.openFileOperationDialog(title, description, actionType);
  }

  private dialogTitle(actionType: TypeOfAction): string {
    return `FILE_MANAGER_${actionType}_BTN_HINT`;
  }

  private dialogDescription(actionType: TypeOfAction, multipleFiles: boolean): string {
    return multipleFiles
      ? `FILE_MANAGER_${actionType}_FILES_DESCRIPTION`
      : `FILE_MANAGER_${actionType}_FILE_DESCRIPTION`;
  }

  private openFileOperationDialog(
    title: string,
    description: string,
    actionType: string,
  ): Observable<string | boolean> {
    return this.notificationService.dialogDefault({
      title: title,
      description: description,
      buttons: [
        this.modalAttributesService.createDialogButton(
          `FILE_MANAGER_${actionType}`,
          'btn-primary',
          DialogActionTypes.CONFIRM_ACTION,
        ),
        ...this.modalAttributesService.getDefaultCloseAttr(),
      ],
    });
  }
}
